import { index, modelOptions, mongoose, post, prop, type Ref } from "@typegoose/typegoose";
import { z } from "zod";
import { ThirdPartyApiServiceType } from "../../Configurations/ThirdPartyApiServiceConfigurations.js";
import { getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import type { Organization } from "../organization/organization.js";
import { ScdDocument } from "../scdDocument/scdDocument.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type {
    ThirdPartyApiCredentialFieldsWithDefaults,
    ThirdPartyApiCredentialSchemaTypes,
} from "../types/thirdPartyApiCredential.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "ThirdPartyApiCredential" as const;

export const THIRD_PARTY_API_CREDENTIAL_COLLECTION_NAME = "thirdPartyApiCredentials" as const;

/**
 * One organization's credentials for one third-party API account. An organization can hold several
 * accounts for the same service and split its devices between them.
 *
 * SCD Type-2: the stable identity across versions is `recordId`, so devices reference the credential
 * by `recordId` — never by `_id`, which changes every version.
 */
@index({ owner: 1, serviceType: 1 }, { partialFilterExpression: { versionIsCurrent: true, deleted: false } })
@index({ owner: 1, name: 1 }, { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: THIRD_PARTY_API_CREDENTIAL_COLLECTION_NAME,
        _id: true,
    },
})
@post<SchemaBase<ThirdPartyApiCredential>>(...addSchemaKeyHook(SCHEMA_KEY))
export class ThirdPartyApiCredential extends ScdDocument<
    ThirdPartyApiCredential,
    ThirdPartyApiCredentialFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /** Taken from the caller's session, never from the body. */
    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: true, ref: "Organization" })
    public owner!: Ref<Organization>;

    /**
     * User-facing label, e.g. "North ranch WeatherLink". Unique per org among live versions; trimmed,
     * but not case-folded, so names differing only in case are still distinct credentials.
     */
    @validate({ zodValidator: z.string().trim().min(1) })
    @prop({ type: () => String, required: true })
    public name!: string;

    @validate({ zodValidator: z.enum(Object.values(ThirdPartyApiServiceType) as [string, ...string[]]) })
    @prop({ type: () => String, enum: ThirdPartyApiServiceType, required: true })
    public serviceType!: ThirdPartyApiServiceType;

    /** Optional here; the service's `credentialFormat` decides whether it is required. */
    @validate({ zodValidator: z.string().min(1).optional() })
    @prop({ type: () => String, required: false })
    public apiKey?: string;

    /**
     * Never returned: every route that serialises a credential must project this out. Left normally
     * selected because SCD carry-forward reads the superseded version unprojected and would drop a
     * `select: false` field on any unrelated edit.
     */
    @validate({ zodValidator: z.string().min(1).optional() })
    @prop({ type: () => String, required: false })
    public apiSecret?: string;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public notes?: string;

    static getters = {} as const satisfies Record<string, (self: ThirdPartyApiCredential) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<ThirdPartyApiCredentialSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<ThirdPartyApiCredential, ThirdPartyApiCredentialFieldsWithDefaults>,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

ThirdPartyApiCredential.dtoValidators = getZodSchemaFromClass(
    ThirdPartyApiCredential,
) as unknown as DtoValidatorTypes<ThirdPartyApiCredentialSchemaTypes>;
